import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import axios from 'axios'
import debounce from 'lodash.debounce'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Paper, Box, Typography, Stack, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, Chip, IconButton, CircularProgress,
  Alert, Divider, List, ListItemButton, ListItemText,
  InputAdornment, Checkbox, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails,
  Collapse,
  useTheme, useMediaQuery,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import SearchIcon from '@mui/icons-material/Search'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EmailIcon from '@mui/icons-material/Email'
import PersonIcon from '@mui/icons-material/Person'
import LinkIcon from '@mui/icons-material/Link'
import { motion } from 'framer-motion'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import ViewHeader from '../components/ViewHeader'
import SuggestedOrderCard from './SuggestedOrderCard'
import BelegtePositionPanel from './BelegtePositionPanel'
import { FS, SP, PANEL_SX, hoverOnly } from '../theme/tokens'
import { v2Tone } from '../theme/theme'
import CaseTime from '../components/common/CaseTime'
import {
  ISSUE_TYPE_LABELS,
  ISSUE_TYPES_NICHT_MANUELL_ANLEGBAR,
  WIZARD_ISSUE_TYPE_ANNOTATION,
  WIZARD_FALLART_CHIP,
  fallartAusIssueType,
  issueTypeAnzeige,
} from './constants'
import { formatBytes, formatMoney } from '../utils/format'
import DisabledReason from '../components/common/DisabledReason';

const API = process.env.REACT_APP_API_BASE_URL


// N2a (2026-09-07): war Sammlung Nr. 10 der Inventur — eine neunte, eigene
// Aufzaehlung derselben Wertemenge. Die Werte und ihre Reihenfolge kommen
// jetzt aus `ISSUE_TYPE_LABELS`, der Ausschluss aus einer BENANNTEN Menge
// (bis dahin war er nirgends im Code begruendet), Etikett und Gruppe aus
// `WIZARD_ISSUE_TYPE_ANNOTATION`. Ergebnis bitgleich zur frueheren Liste;
// `frontend/src/rma/issueTypeOptions.test.js` haelt das fest.
const ISSUE_TYPE_OPTIONS = Object.keys(ISSUE_TYPE_LABELS)
  .filter(v => !ISSUE_TYPES_NICHT_MANUELL_ANLEGBAR.has(v))
  .map(v => ({ value: v, ...WIZARD_ISSUE_TYPE_ANNOTATION[v] }))

// 2026-04-27 — initial-message step removed. Creating a case no longer pushes
// an immediate confirmation/notify email. The case detail's AI Composer is the
// single place to send outbound mail. This avoids the create-time email
// triggering an auto-transition to "Waiting for customer" with no portal link
// (RMA-2026-0393 fallout).
/**
 * N24 — Vorbelegung des Mengenfelds im Assistenten.
 *
 * `''` (leeres Pflichtfeld) bei mehr als einem bestellten Stueck, sonst `1`.
 *
 * 🔴 Warum nicht immer leer: bei bestellt = 1 gibt es nichts zu fragen — die 1
 * ist die einzige moegliche Zahl, und ein Pflichtfeld waere ein Klick ohne
 * Aussage. Warum nicht immer 1: das war der Defekt. Anlassfall RMA-2026-0603.
 */
export function mengenVorbelegung(li) {
  const bestellt = bestellteMenge(li)
  return bestellt !== null && bestellt > 1 ? '' : 1
}

/**
 * Die bestellte Menge einer Position — oder `null`, wenn sie nicht lesbar ist.
 *
 * 🔴 **`Number(null)` ist 0, nicht NaN**, und `Number.isFinite(0)` ist wahr. Eine
 * Pruefung `Number.isFinite(Number(li.quantity))` haelt eine fehlende Menge also
 * fuer eine gemessene Null — dieselbe Falle, die im Erstattungsdialog schon
 * einmal aus einem unlesbaren Wert ein „none" gemacht hat (Regel-16-Gate W7).
 * Hier waere die Wirkung: die Obergrenze klemmt auf 0, und das Feld liesse sich
 * gar nicht mehr gueltig ausfuellen.
 *
 * Gefunden von der Probe, die fuer `mengeIstGueltig` geschrieben wurde — zwei
 * Stellen, dieselbe Ursache, eine Woche auseinander.
 */
function bestellteMenge(li) {
  if (!li || li.quantity === null || li.quantity === undefined) return null
  const zahl = typeof li.quantity === 'number' ? li.quantity : Number.parseInt(li.quantity, 10)
  return Number.isInteger(zahl) ? zahl : null
}

/**
 * N24 — ist die eingegebene Menge gueltig? Die WACHE, als reine Funktion.
 *
 * 🔴 Warum exportiert und nicht inline: das Regel-15-Gate hat gemessen, dass die
 * inline-Fassung **unfaelschbar** war. Drei Mutationen — `>= 1` auf `>= 0`, die
 * Obergrenze gestrichen, und die ganze Bedingung auf `true` — liessen jeweils
 * alle 48 einschlaegigen Jest-Faelle gruen. Gefangen wurde nur das ENTFERNEN des
 * Aufrufs, und das von einer Quelltextprobe, die prueft, dass er DASTEHT — nicht,
 * dass er etwas tut.
 *
 * Das ist woertlich die Klasse, die dieser Zweig selbst in den Backlog schreibt
 * (`docs/backlog-auto-repair/2026-09-17-quelltextproben-sehen-eine-invertierte-bedingung-nicht.md`):
 * fuer den Quellen-Riegel wurde die Regel befolgt, fuer die eigene Wache nicht.
 * Als reine Funktion haengt sie an genau zwei Werten und ist ohne Render pruefbar.
 *
 * ⚠️ Sie ist die ANZEIGE-Wache, nicht die Geldwache. Der Server weist `< 1` und
 * „mehr als bestellt" mit 422 ab (`quantity_below_one`, `quantity_above_ordered`);
 * hier geht es darum, dass das Personal die Zahl bewusst setzt, statt in einen
 * Serverfehler zu laufen.
 *
 * `true` ohne gewaehlte Position: dann gibt es nichts zu bemessen, und der
 * Weiter-Knopf haengt ohnehin an `!selectedLineItem`.
 *
 * ⚠️ **Ohne lesbare Bestellmenge deckelt sie nicht** — und das ist heute NICHT
 * bewacht, mit Grund: der `onChange` daneben klemmt weiter mit dem Rohwert
 * (`Math.min(3, null)` ist 0), die Funktion ist dort also grosszuegiger als die
 * Bedienung. Eine Probe, die die Grosszuegigkeit zusichert, waere vakuoes —
 * sie stand eine Runde lang da und ist entfernt. Beides zusammen gehoert
 * gerichtet, und das ist ein eigener Schritt:
 * `docs/backlog-auto-repair/2026-09-17-unlesbare-bestellmenge-macht-das-mengenfeld-zur-sackgasse.md`.
 * Erreichbar ist der Zweig aus dem Assistenten heute nicht — `search-orders`
 * schreibt Shopifys `Int!` durch.
 */
export function mengeIstGueltig(li, wert) {
  if (!li) return true
  if (wert === '' || wert === null || wert === undefined) return false
  const zahl = Number(wert)
  if (!Number.isInteger(zahl) || zahl < 1) return false
  const bestellt = bestellteMenge(li)
  return bestellt === null || zahl <= bestellt
}

const STEPS_SERVICE = ['Order', 'Product', 'Issue']
const STEPS_RETURN  = ['Order', 'Product', 'Issue']
// 2026-05-19 — Inquiry path: optional order lookup (step 0) then question form (step 1).
// New customers can skip the lookup; existing customers get all contact fields
// pre-filled from the matched Shopify order. RMADetail's InquiryDraftPanel takes
// over after redirect to /rma/<id>. Idea PR #469 + follow-up 2026-05-19.
const STEPS_INQUIRY = ['Order (optional)', 'Question']

// Map ISO country code → preferred customer language. Mirrors the heuristic
// used by InquiryDraftPanel; DE-speaking markets default to 'de', everything
// else to 'en'.
function _languageFromCountry(code) {
  if (!code) return null
  const upper = String(code).toUpperCase()
  if (['DE', 'AT', 'CH', 'LI', 'LU'].includes(upper)) return 'de'
  return 'en'
}

// Compact key/value row used in the match step extracted info card
function SummaryRow({ label, value }) {
  if (!value) return null
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 76, flexShrink: 0, pt: '1px' }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  )
}

// keep in sync with backend _INTERNAL_DOMAINS (inquiry_routes.py / rma_routes.py)
const INTERNAL_EMAIL_DOMAINS = ['brewout.de']
const isInternalEmail = (e) =>
  !!e && INTERNAL_EMAIL_DOMAINS.some(d => e.toLowerCase().trim().endsWith(d))

// Derive the recommended case type from the stored per-email analysis.
// matchCtx.suggested_case_type (row path) wins; else llm_extracted_json.case_type.
// Returns null when there's no analysis or case_type is 'none' (banner hides).
// NOTE: case_type is the type signal ('return'|'service'|'inquiry'|'none');
// issue_type is a severity enum (defective_doa/wrong_item/...) — do NOT read issue_type.
function deriveRecommendedType(matchCtx, panelEmail) {
  const suggested = matchCtx?.suggested_case_type
  if (suggested === 'return' || suggested === 'service' || suggested === 'inquiry') return suggested
  const ext = panelEmail?.llm_extracted_json || {}
  const ct = (ext.case_type || '').toLowerCase()
  if (ct === 'return' || ct === 'service' || ct === 'inquiry') return ct
  return null  // 'none' / missing → no recommendation
}

// Match the LLM's product_name hint to a SINGLE line item on the order.
// SKU exact-match first; else a UNIQUE substring name match (either direction).
// Returns null on no match, ambiguity (>1), or a too-short hint (<4 chars) so we
// never pre-select the wrong item.
function matchLineItemByName(order, hint) {
  const items = order?.line_items || []
  const h = (hint || '').trim().toLowerCase()
  if (!items.length || h.length < 4) return null
  const bySku = items.filter(li => (li.sku || '').trim().toLowerCase() === h)
  if (bySku.length === 1) return bySku[0]
  const byName = items.filter(li => {
    const t = (li.title || '').trim().toLowerCase()
    return t && (t.includes(h) || h.includes(t))
  })
  return byName.length === 1 ? byName[0] : null
}

// Digits-only order-number normaliser so "#10982" / "SHOP-10982" / "10982" match.
const normOrderNumber = s => (s || '').replace(/\D/g, '')

// Create-case section-card entrance — per-card stagger via custom index.
// Same idiom as RecommendedOrdersSection. Reduced-motion handled globally by
// App.js <MotionConfig> (App.js:132); no per-component guard needed.
const CARD_ITEM_VARIANTS = {
  hidden: { opacity: 0, y: 8 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: (i || 0) * 0.06, duration: 0.28, ease: [0, 0, 0.2, 1] },
  }),
}

// Feature-scoped section card for the inquiry wizard step. Bulky, labeled,
// one purpose per card. Not promoted to components/common (single consumer).
// GRILL FIX (warning #2): spread PANEL_SX alone — it already sets responsive
// padding (p:{xs:SP.card, sm:SP.md}); an explicit `p:` override flattens it.
// `index` drives the mount-time stagger delay (per-step, restarts at 0).
function InquirySection({ icon, title, children, index = 0 }) {
  return (
    <Box
      component={motion.div}
      custom={index}
      initial="hidden"
      animate="visible"
      variants={CARD_ITEM_VARIANTS}
      sx={{ ...PANEL_SX }}
    >
      <Stack direction="row" alignItems="center" spacing={SP.tight} sx={{ mb: SP.tight }}>
        {icon}
        <Typography component="h3" variant="subtitle2" fontWeight={700} sx={{ fontSize: FS.lg }}>
          {title}
        </Typography>
      </Stack>
      <Stack spacing={SP.tight}>{children}</Stack>
    </Box>
  )
}

// Step indicator — number circles with labels
function StepIndicator({ activeStep, isReturn, isInquiry, hasMatchStep }) {
  const baseSteps = isInquiry
    ? STEPS_INQUIRY
    : (isReturn ? STEPS_RETURN : STEPS_SERVICE)
  const STEPS = hasMatchStep ? ['Match Order', ...baseSteps] : baseSteps
  return (
    <Stack direction="row" spacing={SP.sm} justifyContent="center" mb={SP.card}>
      {STEPS.map((label, i) => (
        <Stack key={i} alignItems="center" spacing={SP.chip}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: i < activeStep
                ? 'success.main'
                : i === activeStep
                  ? 'primary.main'
                  : 'action.disabled',
              color: 'white',
              fontSize: FS.xs,
              fontWeight: 700,
            }}
          >
            {i < activeStep
              ? <CheckCircleIcon sx={{ fontSize: FS.lg }} />
              : i + 1}
          </Box>
          <Typography
            variant="caption"
            color={i === activeStep ? 'primary' : 'text.secondary'}
            fontWeight={i === activeStep ? 700 : 400}
          >
            {label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

/**
 * Wizard body / state. Three render modes:
 *
 *   1. Panel mode (`isPanelMode = !!onDone`) — embedded in SlidePanel from
 *      RMAInbox. No page chrome, surfaces email summary + wizardInner.
 *
 *   2. Modal mode (`modalMode = true`) — embedded in Dialog/SwipeableDrawer
 *      via RMANewCaseWizardModal. No page chrome, no email summary
 *      (modal is not opened from inbox). Pure wizardInner.
 *
 *   3. Page mode (default — no panel, no modal) — full PageContainer +
 *      ViewHeader + optional inbox reference panel. Used only by the
 *      legacy `/rma/new` deep-link route shim (which now redirects to
 *      the modal). Kept so a future caller can still embed the page
 *      layout if needed.
 *
 * @param onClose   () => void.  Modal mode: closes the modal. Panel mode:
 *                  ignored (panel uses onCancel).
 * @param onCreated (case) => void. Optional. Fires after case creation in
 *                  modal mode (modal closes automatically; goToCase still
 *                  navigates to /rma/<id> for both modes).
 */
export default function RMANewCaseWizardContent({
  // Panel mode — provided by RMAInbox when opening from SlidePanel
  onDone,                  // (createdCase) => void  — called after email action
  onCancel,                // () => void              — called on cancel
  panelInboxId,            // inbox email id (overrides searchParams)
  panelEmail,              // full inbox email object for the summary card
  panelCtx,                // context object from inbox row (suggested_orders, extracted)
  panelInitialOrder,       // pre-fill order search
  panelInitialIssueType,   // pre-fill issue type
  panelInitialDescription, // pre-fill description
  // Modal mode — provided by RMANewCaseWizardModal
  modalMode = false,
  onClose,                 // () => void — close the modal
  onCreated,               // (case) => void — optional after-create callback
} = {}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Panel mode when caller provides onDone
  const isPanelMode = !!onDone

  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // ── N33: Bestellung plus Produkt schon belegt ────────────────────────────
  const [belegtVon, setBelegtVon] = useState(null)
  const [nochmalSenden, setNochmalSenden] = useState(null)

  // Inbox pre-fill + context (only in page mode; panel mode has inbox visible)
  const inboxId = panelInboxId ?? (searchParams.get('inbox_id') || null)
  const [inboxCtx, setInboxCtx] = useState(null)
  const [inboxCtxLoading, setInboxCtxLoading] = useState(!isPanelMode && !!inboxId)

  useEffect(() => {
    if (isPanelMode || !inboxId) return
    setInboxCtxLoading(true)
    axios.get(`${API}/api/rma/inbox/${inboxId}/context`)
      .then(r => { setInboxCtx(r.data); setInboxCtxLoading(false) })
      .catch(err => {
        console.error('[RMA] inbox context fetch failed', err)
        setInboxCtxLoading(false)
        setInboxCtx({ email: {}, extracted: {}, suggested_orders: [] })
      })
  }, [inboxId]) // eslint-disable-line

  // AI-generated case description — pre-fills the "Describe the issue" field
  // when the wizard opens from an inbox email. User can edit before saving.
  const [aiDescLoading, setAiDescLoading] = useState(false)
  const [aiDescSource, setAiDescSource] = useState(null)  // 'llm' | 'template' | null
  useEffect(() => {
    if (!inboxId) return
    setAiDescLoading(true)
    axios.get(`${API}/api/rma/inbox/${inboxId}/ai-description`)
      .then(r => {
        const text = (r.data?.description || '').trim()
        if (text) {
          // Only pre-fill if the user hasn't already typed something
          setIssueDescription(prev => prev?.trim() ? prev : text)
          setAiDescSource(r.data?.source || 'llm')
        }
      })
      .catch(err => console.warn('[RMA] ai-description failed', err))
      .finally(() => setAiDescLoading(false))
  }, [inboxId]) // eslint-disable-line

  // Match Order pre-step — shown when suggested orders are available from inbox context
  const matchCtx = panelCtx || inboxCtx
  const hasMatchStep = !!(panelCtx?.suggested_orders?.length > 0)
  const [matchPhase, setMatchPhase] = useState(hasMatchStep)

  // Recommendation banner dismiss state — collapsed when user accepts or picks another type
  const [recoDismissed, setRecoDismissed] = useState(false)

  // Step 1 — order search
  const [searchQ, setSearchQ] = useState(panelInitialOrder || searchParams.get('order') || '')
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [selectedOrder, setSelectedOrder] = useState(null)
  // B1-rest: true when the order was auto-seeded from the inbound email extraction
  // (suggested-order accept, banner silent pre-select, or ctx=null auto-search) rather
  // than manually searched/picked. Drives the "Matched from email" hint on step 1.
  const [orderFromEmail, setOrderFromEmail] = useState(false)
  // Reveal the blank order search when the staff rejects the suggested-order card
  // (default: show the SuggestedOrderCard when a suggestion exists — B1 invariant).
  const [orderSearchOpen, setOrderSearchOpen] = useState(false)

  // Step 2 — product selection
  const [selectedLineItem, setSelectedLineItem] = useState(null)
  // 🔴 N24: leer statt `1`. Bis 2026-09-17 stand hier `useState(1)`, und drei
  // weitere Stellen setzten stumm auf 1 zurueck. Bei mehr als einem bestellten
  // Stueck war die 1 damit eine Behauptung, die niemand getroffen hat — der
  // Anlassfall RMA-2026-0603 (4 bestellt, 4 zurueck, Fall fuehrte 1, POG
  // erstattete 2,25 statt 26,70 EUR). Ohne Position gibt es nichts
  // vorzubelegen; `mengenVorbelegung` entscheidet, sobald eine gewaehlt ist.
  const [quantityAffected, setQuantityAffected] = useState('')
  const [quantityTouched, setQuantityTouched] = useState(false)
  // W3 (Regel-16-Gate): nach einer Abweisung auf dem Sendeort wird von Schritt 2
  // auf Schritt 1 gesprungen. Der Knopf, auf dem der Fokus stand, wird dabei
  // abgebaut — ohne Ziel faellt der Fokus auf `<body>`, und wer mit der Tastatur
  // arbeitet, hoert den Grund und muss sich den Weg zum Feld neu suchen.
  const mengeRef = useRef(null)
  const [issueType, setIssueType] = useState(panelInitialIssueType || searchParams.get('issue_type') || '')

  // Step 3 — issue details
  const [issueDescription, setIssueDescription] = useState(
    panelInitialDescription || searchParams.get('description') || '',
  )
  const [evidenceUrls, setEvidenceUrls] = useState([''])
  const [serialNumbers, setSerialNumbers] = useState([''])

  // Inquiry path (no order, no product) — standalone customer + question fields.
  // Idea PR #469 / 2026-05-19. Form replaces Order + Product steps.
  const [inquiryCustomerName, setInquiryCustomerName] = useState('')
  const [inquiryCustomerEmail, setInquiryCustomerEmail] = useState('')
  const [inquiryCustomerPhone, setInquiryCustomerPhone] = useState('')
  const [inquiryCustomerLanguage, setInquiryCustomerLanguage] = useState('de')
  const [inquiryCustomerStreet, setInquiryCustomerStreet]   = useState('')
  const [inquiryCustomerLine2, setInquiryCustomerLine2]     = useState('')
  const [inquiryCustomerZip, setInquiryCustomerZip]         = useState('')
  const [inquiryCustomerCity, setInquiryCustomerCity]       = useState('')
  const [inquiryCustomerCountry, setInquiryCustomerCountry] = useState('')
  const [inquiryOrderRef, setInquiryOrderRef] = useState('')
  // Subject / Title — required. Stored as product_title (reuses the existing
  // NOT NULL column so no new schema). Becomes the case headline in the RMA
  // Overview list. Per Samuel 2026-05-20.
  const [inquirySubject, setInquirySubject] = useState('')

  // LLM-Assist for inquiry cases (2026-07-04). Staff pastes the original email
  // (or the forwarded body) and the analyzer populates subject / summary /
  // language / customer identity + a checkbox list of matched products (SKU).
  // Selected matches get persisted as case.linked_product_refs so relations +
  // learning downstream can query "which cases mentioned SKU X" cheaply.
  const [aiEmailText, setAiEmailText] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)
  const [aiExpanded, setAiExpanded] = useState(false)
  const [productMatches, setProductMatches] = useState([])
  const [selectedRefSkus, setSelectedRefSkus] = useState(() => new Set())

  // Guards Pillar-1 auto-seed so it fills inquiry fields once per inbox entry —
  // re-armed when the user switches out of inquiry (see switchIssueType).
  const seededFromInboxRef = useRef(false)

  // B1: type-acceptance signal for the ctx=null order auto-select. issueType is
  // mount-seeded from the SEVERITY enum (issue_type), so it cannot distinguish
  // "LLM guessed a severity" from "staff chose a case type". This flips true only
  // when the user actively picks return/service (banner Accept or a tile) via
  // switchIssueType — so the auto-select never fires on mount, only after accept.
  const [typeAccepted, setTypeAccepted] = useState(false)
  // One-shot guards for the auto-seed effects (reset per email via key={wizardEmail.id}).
  const orderAutoSelectedRef = useRef(false)  // ctx=null free-text order → auto-select once
  const lastSeededOrderRef   = useRef(null)   // product auto-select: last order id attempted

  // N2b/R1: liest die BENANNTE Ableitung statt eines eigenen Vergleichs.
  // Wertgleich (`fallartAusIssueType(v) === 'inquiry'` gilt genau fuer
  // `v === 'inquiry'`), aber es bleibt keine zweite Gestalt desselben Begriffs
  // stehen — die dritte Kachel liest `isInquiry`, waehrend die zwei anderen
  // schon die Ableitung lasen. Befund des Regel-15-Gates, 07.09.
  const isInquiry = fallartAusIssueType(issueType) === 'inquiry'

  // Recommendation banner state — hoisted so BOTH the banner (below) and the
  // Case-Type tiles gate on it: while the banner is the primary path the tiles
  // stay hidden (revealed via the banner's "Other type" fallback). bannerActive
  // is false whenever there's no recommendation/email, so tiles always show then.
  const recType = deriveRecommendedType(matchCtx, panelEmail)
  const bannerActive = !!recType && !!panelEmail && !recoDismissed
  // Detected product name (LLM) — used to pre-select the matching line item.
  const productHint = (panelEmail?.llm_extracted_json?.product_name || '').trim()

  // Wrap setIssueType so inquiry-specific state resets when switching out of
  // inquiry mode (return/service tile). Prevents stale form data on tile-toggle.
  // Also reset the step + order picker so the user lands back on step 0 with
  // a fresh order lookup (inquiry's lookup is OPTIONAL, return/service's is
  // REQUIRED — same UI but different downstream contract).
  const switchIssueType = (newType) => {
    const wasInquiry = fallartAusIssueType(issueType) === 'inquiry'   // N2b/R1
    const willBeInquiry = fallartAusIssueType(newType) === 'inquiry'  // N2b/R1
    if (wasInquiry !== willBeInquiry) {
      // Crossing the inquiry boundary — clear inquiry form, order pick, and
      // search results so neither path inherits stale state from the other.
      setInquiryCustomerName('')
      setInquiryCustomerEmail('')
      setInquiryCustomerPhone('')
      setInquiryCustomerLanguage('de')
      setInquiryCustomerStreet(''); setInquiryCustomerLine2(''); setInquiryCustomerZip('')
      setInquiryCustomerCity(''); setInquiryCustomerCountry('')
      setInquiryOrderRef('')
      setInquirySubject('')
      setIssueDescription('')
      setAiEmailText('')
      setAiError(null)
      setAiExpanded(false)
      setProductMatches([])
      setSelectedRefSkus(new Set())
      setSelectedOrder(null)
      setSelectedLineItem(null)
      setSearchResults([])
      setStep(0)
      seededFromInboxRef.current = false
    }
    setIssueType(newType)
    // Arm the ctx=null order auto-select only on an active return/service pick
    // (banner Accept or a tile). Inquiry / empty leaves it disarmed.
    setTypeAccepted(newType === 'return' || newType === 'other')
  }

  // Analyze pasted email → populate inquiry fields + product matches.
  // Only fills empty fields so staff edits are never clobbered.
  const handleAnalyzeInquiryEmail = async () => {
    const text = aiEmailText.trim()
    if (text.length < 20) {
      setAiError('At least 20 characters required')
      return
    }
    setAiLoading(true)
    setAiError(null)
    try {
      const payload = {
        email_body: text,
        // Wizard already has line items when an order is picked — hand them
        // over so the backend can match without a re-fetch.
        order_line_items: (selectedOrder?.line_items || selectedOrder?.lineItems || [])
          .map(li => ({
            title: li.title || li.name,
            sku: li.sku || li.variant?.sku,
            product_id: li.product_id || li.product?.id,
          })),
      }
      const res = await axios.post(`${API}/api/inquiry/analyze-email-content`, payload)
      const d = res.data || {}

      // Only populate empty fields — never overwrite staff edits.
      if (d.title && !inquirySubject.trim()) setInquirySubject(d.title)
      if (d.summary && !issueDescription.trim()) setIssueDescription(d.summary)
      if (d.customer_name && !inquiryCustomerName.trim()) setInquiryCustomerName(d.customer_name)
      if (d.customer_email && !isInternalEmail(d.customer_email) && !inquiryCustomerEmail.trim())
        setInquiryCustomerEmail(d.customer_email)
      if (d.customer_phone && !inquiryCustomerPhone.trim()) setInquiryCustomerPhone(d.customer_phone)
      if (d.customer_street && !inquiryCustomerStreet.trim()) setInquiryCustomerStreet(d.customer_street)
      if (d.customer_zip && !inquiryCustomerZip.trim()) setInquiryCustomerZip(d.customer_zip)
      if (d.customer_city && !inquiryCustomerCity.trim()) setInquiryCustomerCity(d.customer_city)
      if (d.customer_country && !inquiryCustomerCountry.trim()) setInquiryCustomerCountry(d.customer_country)
      if (d.order_reference && !inquiryOrderRef.trim()) setInquiryOrderRef(d.order_reference)
      if (d.language && (d.language === 'de' || d.language === 'en')) setInquiryCustomerLanguage(d.language)

      // Product matches — default all selected. Staff can uncheck what
      // doesn't apply before submitting.
      const matches = Array.isArray(d.product_matches) ? d.product_matches : []
      setProductMatches(matches)
      setSelectedRefSkus(new Set(matches.map(m => m.sku).filter(Boolean)))
    } catch (e) {
      setAiError(e?.response?.data?.error || 'Analysis failed')
    } finally {
      setAiLoading(false)
    }
  }

  const toggleProductRef = (sku) => {
    setSelectedRefSkus(prev => {
      const next = new Set(prev)
      if (next.has(sku)) next.delete(sku)
      else next.add(sku)
      return next
    })
  }

  // Attachments proposed from the inbound email — pre-checked, user can opt out
  const proposedAttachments = useMemo(() => matchCtx?.email?.attachments || [], [matchCtx?.email?.attachments])
  const [attachmentIds, setAttachmentIds] = useState(null) // Set<string> | null (null = "all")
  useEffect(() => {
    if (proposedAttachments.length && attachmentIds === null) {
      setAttachmentIds(new Set(proposedAttachments.map(a => a.id)))
    }
  }, [proposedAttachments, attachmentIds])
  const toggleAttachment = id => {
    setAttachmentIds(prev => {
      const next = new Set(prev || [])
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Pillar 1 — when the inquiry branch is active AND the wizard was opened from
  // an inbox email, seed empty inquiry fields from the already-parsed message.
  // Only fills blanks (never clobbers staff edits); no LLM call. The analyzer
  // button stays available for optional product-match enrichment.
  //
  // GRILL FIX (blocker #1): identity fields must fall back to
  // panelEmail.llm_extracted_json — matchCtx is null on the InboxCard "Create
  // Case" path (RMAInbox.jsx passes ctx=null; panel mode never fetches inboxCtx).
  // This mirrors the file's own convention at ~line 1603 and RMAInbox.jsx:232.
  useEffect(() => {
    if (!isInquiry || !panelEmail || seededFromInboxRef.current) return
    const ext = matchCtx?.extracted || panelEmail.llm_extracted_json || {}
    const bodyForAnalyzer = panelEmail.body_text || panelEmail.body_preview || ''
    setInquirySubject(prev => prev.trim() ? prev : (panelEmail.subject || ''))
    setIssueDescription(prev => prev.trim() ? prev : (panelEmail.llm_summary || ''))
    setInquiryCustomerName(prev => prev.trim() ? prev : (ext.customer_name || ''))
    const seedEmail = ext.customer_email
    if (seedEmail && !isInternalEmail(seedEmail)) {
      setInquiryCustomerEmail(prev => prev.trim() ? prev : seedEmail)
    }
    setInquiryCustomerPhone(prev => prev.trim() ? prev : (ext.customer_phone || ''))
    // GRILL W1: `extracted` exposes customer_street (not customer_address_line1) — live key first.
    setInquiryCustomerStreet(prev => prev.trim() ? prev : (ext.customer_street || ext.customer_address_line1 || ''))
    setInquiryCustomerZip(prev    => prev.trim() ? prev : (ext.customer_zip || ''))
    setInquiryCustomerCity(prev   => prev.trim() ? prev : (ext.customer_city || ''))
    setInquiryCustomerCountry(prev=> prev.trim() ? prev : (ext.customer_country || ''))
    setAiEmailText(prev => prev.trim() ? prev : bodyForAnalyzer)
    // GRILL FIX (warning #1): no `language` field on context — derive from country
    // via the existing _languageFromCountry helper (imported, used ~line 420).
    const derivedLang = _languageFromCountry(ext.customer_country)
    if (derivedLang === 'de' || derivedLang === 'en') {
      setInquiryCustomerLanguage(derivedLang)
    }
    seededFromInboxRef.current = true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInquiry, panelEmail, matchCtx])
  // setters are stable; effect intentionally keyed on inquiry-entry + email identity

  // Step 3 — file upload state
  const fileInputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState([]) // [{key, name, uploading, url, error}]

  // Created case is still tracked for the goToCase navigation hand-off.
  const [createdCase, setCreatedCase] = useState(null)

  // ── Step handlers ──────────────────────────────────────────────

  // Universal live-search debounce for ALL 3 tiles (Return / Service / Inquiry)
  // at step 0. No explicit Search button — typing 2+ chars fires after 300ms.
  // Pattern mirrors SidebarAndHeaderBar global search (300ms debounce,
  // silent error swallow). 2-char minimum matches useProductSearch.js.
  // 2026-05-20: generalized from inquiry-only (was fetchInquirySearchResults)
  // to all tiles. Single consistent UX across all case types.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOrderSearchResults = useCallback(debounce(async q => {
    if (!q || q.trim().length < 2) {
      setSearchResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    try {
      const res = await axios.get(`${API}/api/rma/shopify/search-orders`, {
        params: { q: q.trim() },
      })
      setSearchResults(res.data.orders || [])
    } catch {
      setSearchResults([])
    } finally {
      setSearching(false)
    }
  }, 300), [])

  useEffect(() => {
    if (step === 0 && !matchPhase) {
      fetchOrderSearchResults(searchQ)
    }
    return () => { fetchOrderSearchResults.cancel?.() }
  }, [searchQ, step, matchPhase, fetchOrderSearchResults])

  const handleSelectOrder = (order, fromEmail = false) => {
    setSelectedOrder(order)
    setOrderFromEmail(fromEmail)
    setSelectedLineItem(null)
    setQuantityAffected('')
    setQuantityTouched(false)
    setStep(1)
    setError(null)
  }

  // Inquiry-specific order pick: pre-fill the customer fields from the
  // matched order so the question step opens with all contact info ready,
  // then advance to the Question step. Skip clears any prior selection so
  // the new-customer payload stays clean.
  const handleSelectOrderForInquiry = order => {
    setSelectedOrder(order)
    setInquiryCustomerName(order.customer_name || '')
    setInquiryCustomerEmail(order.customer_email || '')
    setInquiryCustomerPhone(order.customer_phone || '')
    setInquiryCustomerLanguage(_languageFromCountry(order.customer_country) || 'de')
    setInquiryCustomerStreet(order.customer_address_line1 || '')
    setInquiryCustomerLine2(order.customer_address_line2 || '')
    setInquiryCustomerZip(order.customer_zip || '')
    setInquiryCustomerCity(order.customer_city || '')
    setInquiryCustomerCountry(order.customer_country || '')
    setInquiryOrderRef(order.name || '')
    setStep(1)
    setError(null)
  }

  const handleSkipInquiryOrderLookup = () => {
    setSelectedOrder(null)
    setSearchResults([])
    setSearchQ('')
    setStep(1)
    setError(null)
  }

  // B1: auto-select the line item matching the detected product name, once per
  // order. Pre-fill only — never overrides a manual pick; re-fires when staff
  // pick a DIFFERENT order (lastSeededOrderRef holds the last attempted order id).
  useEffect(() => {
    if (!selectedOrder || selectedLineItem || isInquiry || !productHint) return
    if (lastSeededOrderRef.current === selectedOrder.id) return
    lastSeededOrderRef.current = selectedOrder.id
    const li = matchLineItemByName(selectedOrder, productHint)
    // 🔴 Der Auto-Treffer wird GLEICH behandelt (Owner-Frage 3 im N24-Plan): hier
    // waehlt nicht der Mensch, sondern ein Namensvergleich. Ist die Position
    // geraten, waere die Menge es auch — ein leeres Pflichtfeld kostet einen
    // Tastendruck, eine falsche 1 kostet eine Handerstattung.
    if (li) { setSelectedLineItem(li); setQuantityAffected(mengenVorbelegung(li)); setQuantityTouched(false) }
  }, [selectedOrder, selectedLineItem, isInquiry, productHint])

  // B1: ctx=null (InboxCard) path has no suggested_orders, but the detected
  // order_number auto-searches (searchQ = panelInitialOrder). AFTER staff accept a
  // return/service type, auto-select the order iff exactly one search result's
  // number matches the detected one. One-shot; never hijacks a manual search
  // (exact digit-match to panelInitialOrder only). Setting the order cascades into
  // the product auto-select effect above.
  useEffect(() => {
    if (orderAutoSelectedRef.current || !typeAccepted) return
    if (selectedOrder || matchPhase || step !== 0 || !panelInitialOrder) return
    const wanted = normOrderNumber(panelInitialOrder)
    if (!wanted) return
    const hits = searchResults.filter(o => normOrderNumber(o.name) === wanted)
    if (hits.length === 1) {
      orderAutoSelectedRef.current = true
      handleSelectOrder(hits[0], true)  // auto-seeded from email order number
    }
    // handleSelectOrder + panelInitialOrder stable per mount; guards + one-shot ref prevent re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, typeAccepted, selectedOrder, matchPhase, step])

  // ── N24: Mengenpflicht, abgeleitet ────────────────────────────────────────
  // `mengePflicht` nur bei bestellt > 1 — siehe `mengenVorbelegung`.
  // `mengeGueltig` ist die WACHE und sitzt an zwei Stellen: am Weiter-Knopf
  // (Anzeige) und in `handleProceedToStep4` (dem Sendeort).
  //
  // 🔴 **Berichtigung 2026-09-17 (Regel-15-Wiedervorlage A4).** Hier stand als
  // Begruendung: Step-2-Back springe auf Step 1 zurueck, ohne zu raeumen — und
  // **Fall 4 von `new-case-menge.spec.ts` misst das Gegenteil**: Back bewahrt
  // die Menge, Next bleibt frei. Der eigene Test widerlegte die eigene
  // Begruendung.
  //
  // Der Sendeort ist **Tiefenverteidigung**, und das gehoert ehrlich gesagt:
  // ueber die heutige Oberflaeche ist er nicht erreichbar, weil man Schritt 2
  // nur mit gueltiger Menge betritt. Er traegt den Fall, dass jemand einen
  // Schritt einbaut, der die Position SPAETER aendert.
  const mengeBestellt = Number(selectedLineItem?.quantity)
  const mengePflicht = Number.isFinite(mengeBestellt) && mengeBestellt > 1
  // 🔴 Die Wache steht in `mengeIstGueltig` (oben, exportiert) — nicht inline.
  // Regel-15-Gate B1: die inline-Fassung war unfaelschbar, drei Mutationen
  // liessen 48 von 48 Jest-Faellen gruen. Begruendung am Funktionskopf.
  const mengeGueltig = mengeIstGueltig(selectedLineItem, quantityAffected)
  const mengeFehler = !!selectedLineItem && quantityTouched && !mengeGueltig

  const handleProceedToStep3 = () => {
    if (!selectedLineItem || !issueType) return
    if (!mengeGueltig) { setQuantityTouched(true); return }
    setStep(2)
    setError(null)
  }

  /**
   * Der EINE Weg, auf dem ein Fall angelegt wird.
   *
   * 🔴 Warum das eine Funktion ist und nicht zwei Zweige: die Datei hatte ZWEI
   * `axios.post(.../cases)` mit je eigenem `catch`, und die Regel-14-Runde hat
   * gemessen, dass ein 409-Zweig an nur einer Stelle die andere still
   * durchlaufen liesse. Zwei Fehlerpfade, die uebereinstimmen muessen, sind der
   * Defekt, nicht die Loesung.
   *
   * Gibt den angelegten Fall zurueck, oder `null`, wenn nichts entstanden ist.
   */
  const fallAnlegen = async (nutzlast, erzwingen = false) => {
    const pfad = `${API}/api/rma/cases${erzwingen ? '?force=true' : ''}`
    try {
      const res = await axios.post(pfad, nutzlast)
      setBelegtVon(null)
      setNochmalSenden(null)
      return res.data?.case || res.data
    } catch (e) {
      // N33: 409 mit diesem Kode ist KEIN Fehlschlag, sondern eine Auskunft mit
      // zwei Wegen. Jeder andere Fehler bleibt ein roter Kasten wie bisher.
      if (e.response?.status === 409 && e.response?.data?.code === 'bestellung_produkt_belegt') {
        setBelegtVon(e.response.data.belegt_von || [])
        // 🔴 Die doppelte Pfeilfunktion ist Absicht. `setState` behandelt ein
        // Funktionsargument als AKTUALISIERER und riefe es sofort auf; die
        // aeussere gibt die innere zurueck, und erst die ist der Rueckruf.
        setNochmalSenden(() => () => fallAnlegen(nutzlast, true))
        return null
      }
      setError(e.response?.data?.error || 'Failed to create case')
      return null
    }
  }

  const handleProceedToStep4 = async () => {
    // 🔴 Die zweite Wache — hier geht die Nutzlast raus. Ueber die heutige
    // Oberflaeche ist dieser Zweig **nicht erreichbar** (Schritt 2 betritt man
    // nur mit gueltiger Menge; Back bewahrt sie, ein Positionswechsel setzt sie
    // zurueck und man steht wieder in Schritt 1). Tiefenverteidigung fuer einen
    // kuenftigen Umbau — Begruendung bei `mengeGueltig`.
    if (!mengeGueltig) {
      setQuantityTouched(true)
      setStep(1)
      setError('Please confirm how many units came back.')
      // W3: Fokus auf das Feld, das den Weg blockiert. Ohne ihn landet er auf
      // `<body>`, und der naechste Tabulatorschlag beginnt am Seitenanfang.
      // `setTimeout(0)`, weil Schritt 1 erst nach dem Neuzeichnen existiert.
      setTimeout(() => mengeRef.current?.focus(), 0)
      return
    }
    setSaving(true)
    setError(null)
    try {
      const angelegt = await fallAnlegen({
        shopify_order_id:    selectedOrder.id,
        shopify_order_name:  selectedOrder.name,
        shopify_line_item_id: selectedLineItem.id,
        product_title:       selectedLineItem.title,
        variant_title:       selectedLineItem.variant_title || null,
        sku:                 selectedLineItem.sku || null,
        shopify_vendor_name: selectedLineItem.vendor || null,
        quantity_affected:   Number(quantityAffected),
        // 🔴 Die MULTIPLIKATION entfaellt hier — der Server rechnet
        // (`rma_routes.py:5861-5872`, „Der Stueckpreis kommt vom Waehler, die
        // Menge multipliziert der Server"). Bis 2026-09-17 stand hier
        // `selectedLineItem.unit_price * quantityAffected` und
        // `line_item_price_eur`; damit war der serverseitige Zweig vom
        // Assistenten aus UNERREICHBAR und der Kommentar dort beschrieb eine
        // Absicht, nicht den Code.
        //
        // ⚠️ `|| undefined` ist nicht Kosmetik: `search-orders` emittiert
        // `unit_price` IMMER als Zahl (Rueckfall `float(... or 0)`,
        // `rma_routes.py:5550-5553`), und `0 in (None, "")` ist serverseitig
        // `False` — ohne den Wahrheitswert-Filter schriebe ein Freiposten
        // `line_item_price_eur = 0.00` dorthin, wo heute NULL steht. Eine 0
        // ueberlebt jede `is None`-Wache auf dem Geldweg. Die alte Zeile hatte
        // diesen Schutz als `x ? … : undefined`; er bleibt.
        unit_price_eur:      selectedLineItem.unit_price || undefined,
        // 🔴 **KEIN `quantity_source` — Owner-Entscheid 2026-09-17**, und der
        // Grund ist ein anderer als der, der hier eine Fassung lang stand.
        //
        // Nicht „kein neuer Geldweg vor B3" (das war durch die Sperre
        // `menge_ueber_eins_auto` ohnehin ueberholt), sondern: **der Server
        // bestimmt die Herkunft selbst.** `POST /cases` nimmt das Feld gar nicht
        // mehr entgegen und leitet ab — eine Menge ueber diese Route ist eine
        // Personaleingabe, also `staff`; ohne Mengenangabe `default`.
        //
        // Damit ist §E3 Punkt 1 erfuellt (die bestaetigte Menge schaltet das Tor
        // frei), ohne dass ein Client das Tor oeffnen kann. Ein Wert hier waere
        // wirkungslos — und eine wirkungslose Zeile in einer Geldnutzlast ist
        // schlimmer als keine, weil der naechste Leser sie fuer wirksam haelt.
        issue_type:          issueType,
        issue_description:   issueDescription.trim() || null,
        evidence_urls:       evidenceUrls.filter(u => u.trim()),
        serial_numbers:      serialNumbers.filter(s => s.trim()),
        customer_shopify_id: selectedOrder.customer_id,
        customer_name:       selectedOrder.customer_name,
        customer_email:      selectedOrder.customer_email,
        // Shipping address — the backend (`create_case`) has accepted and
        // persisted these since migration 070, and both order sources already
        // carry them (GET /shopify/search-orders and, as of this change, the
        // inbox context's suggested_orders). This branch simply never sent
        // them, which is why `manual` cases showed up address-less while
        // `lsm` / `shopify` did not. `handleSubmitInquiry` below has sent the
        // five address fields from `selectedOrder` since this file's first
        // commit (it takes the phone from its own typed field instead — that
        // branch has a phone input, this one does not).
        //
        // Snapshot semantics are intended: these are the values as of the
        // order search, not of the save. Note what that does NOT buy you —
        // `refresh_customer_contact` only fills fields that are still empty
        // (`if force or not current or is_internal_garbage`), and neither UI
        // button passes `force`. So once this payload populates the address
        // there is currently no UI path to re-sync it against a LATER Shopify
        // change; the button's remaining job is the case where the data never
        // arrived at all. See docs/backlog-auto-repair/2026-08-06-rma-address-remaining-gaps.md §5.
        customer_phone:         selectedOrder.customer_phone || null,
        customer_address_line1: selectedOrder.customer_address_line1 || null,
        customer_address_line2: selectedOrder.customer_address_line2 || null,
        customer_zip:           selectedOrder.customer_zip || null,
        customer_city:          selectedOrder.customer_city || null,
        customer_country:       selectedOrder.customer_country || null,
        order_date:          selectedOrder.created_at,
        inbox_id:            inboxId ? parseInt(inboxId, 10) : undefined,
        attachment_ids:      attachmentIds ? Array.from(attachmentIds) : undefined,
      })
      // `null` heisst: entweder ein roter Fehler, oder die Belegungskarte steht
      // jetzt und wartet auf die Entscheidung. Beides ist KEIN Weiterlaufen.
      if (!angelegt) return
      setCreatedCase(angelegt)

      // Fire-and-forget: pre-generate a customer-facing draft so the
      // composer on the case-detail opens with an AI reply ready. Works
      // for all case_types now (return, service, inquiry). Runs in the
      // background so navigation isn't blocked; failure is logged but
      // never blocks case creation. See composer_routes.generate_case_draft.
      const createdId = angelegt?.id || angelegt?.case?.id
      if (createdId) {
        axios.post(`${API}/api/composer/generate-case-draft`, {
          case_id: createdId,
          body_text: issueDescription.trim() || null,
        }).catch(err => console.warn('[RMA] pre-gen draft failed (non-blocking)', err))
      }

      goToCase(angelegt)
    } catch (e) {
      // 🔴 W2 des Regel-15-Tors. `fallAnlegen` faengt NUR den `axios.post`.
      // Im `try` stehen weiterhin der Nutzlastaufbau, `setCreatedCase` und vor
      // allem `goToCase`, das den Rueckruf des Aufrufers und `navigate` ruft.
      // Wirft eine davon, war das bis zur Umstellung ein roter Kasten; ohne
      // diesen Zweig waere es eine unbehandelte Zusage, `finally` entsperrte
      // den Knopf, und der Bediener saehe NICHTS.
      //
      // Der 409 kommt hier nie an: `fallAnlegen` behandelt ihn und gibt `null`
      // zurueck, und die Zeile darueber steigt dann aus.
      setError(e.response?.data?.error || 'Failed to create case')
    } finally {
      setSaving(false)
    }
  }

  // Navigate to the created case.
  //   Panel mode → delegate to the caller (RMAInbox handles its own flow).
  //   Modal mode → fire onCreated callback (modal will close + handle side
  //     effects), then navigate to the case detail.
  //   Page mode  → navigate directly.
  const goToCase = (c) => {
    const target = c || createdCase
    if (isPanelMode) {
      onDone(target)
      return
    }
    if (modalMode) {
      // Fire callback BEFORE navigate so the parent can close the modal
      // (or do anything else) before the new page mounts.
      onCreated?.(target)
    }
    navigate(`/rma/${target.id}`)
  }

  // Inquiry submit — minimal payload by default; if the user picked a Shopify
  // order at the optional lookup step, also ship the order-link fields so the
  // case is wired to the order (and downstream features — return-shipping
  // calc, vendor reports, customer history — can resolve it). Backend
  // create_case accepts these as optional for issue_type='inquiry'.
  // RMADetail mounts InquiryDraftPanel automatically; AI draft generation
  // happens there, not here.
  const handleSubmitInquiry = async () => {
    if (
      !inquiryCustomerEmail.trim() ||
      !issueDescription.trim() ||
      !inquirySubject.trim()
    ) return
    setSaving(true)
    setError(null)
    try {
      const payload = {
        issue_type:        'inquiry',
        customer_name:     inquiryCustomerName.trim() || null,
        customer_email:    inquiryCustomerEmail.trim(),
        customer_phone:    inquiryCustomerPhone.trim() || null,
        customer_address_line1: inquiryCustomerStreet.trim() || null,
        customer_address_line2: inquiryCustomerLine2.trim() || null,
        customer_zip:           inquiryCustomerZip.trim() || null,
        customer_city:          inquiryCustomerCity.trim() || null,
        customer_country:       inquiryCustomerCountry.trim() || null,
        customer_language: inquiryCustomerLanguage || 'de',
        issue_description: issueDescription.trim(),
        // Subject becomes the case headline (RMA Overview displays
        // product_title as the case title). Reuses the existing NOT NULL
        // column — no new schema. Backend now requires product_title for
        // inquiry-type cases (matches what the wizard sends).
        product_title:     inquirySubject.trim(),
        // Free-text order reference — preserved on the case for context even
        // when no Shopify order was matched.
        shopify_order_name: selectedOrder?.name || inquiryOrderRef.trim() || null,
        inbox_id:          inboxId ? parseInt(inboxId, 10) : undefined,
        // Multi-product references picked from the AI analysis. Backend
        // (migration 142) sanitises + persists as case.linked_product_refs.
        linked_product_refs: productMatches
          .filter(m => m.sku && selectedRefSkus.has(m.sku))
          .map(m => ({
            sku: m.sku,
            product_id: m.product_id,
            product_title: m.product_title,
            confidence: m.confidence,
            source: m.source || 'wizard_pick',
          })),
      }

      // Order-link fields — only when a Shopify order was picked at the
      // optional lookup step. Otherwise the new-customer path stays clean.
      if (selectedOrder) {
        payload.shopify_order_id       = selectedOrder.id || null
        payload.customer_shopify_id    = selectedOrder.customer_id || null
        payload.customer_address_line1 = selectedOrder.customer_address_line1 || null
        payload.customer_address_line2 = selectedOrder.customer_address_line2 || null
        payload.customer_zip           = selectedOrder.customer_zip || null
        payload.customer_city          = selectedOrder.customer_city || null
        payload.customer_country       = selectedOrder.customer_country || null
        payload.order_date             = selectedOrder.created_at || null
      }

      const created = await fallAnlegen(payload)
      if (!created) return
      setCreatedCase(created)

      // Pre-generate customer draft in the background so the composer opens
      // ready. Same universal endpoint as return/service — the backend
      // derives inquiry_type from case.inquiry_type here.
      const createdId = created?.id
      if (createdId) {
        axios.post(`${API}/api/composer/generate-case-draft`, {
          case_id: createdId,
          body_text: issueDescription.trim() || null,
          inquiry_type: created?.inquiry_type || null,
          language: created?.customer_language || null,
        }).catch(err => console.warn('[RMA] pre-gen inquiry draft failed (non-blocking)', err))
      }

      goToCase(created)
    } catch (e) {
      // Siehe den Zweig oben. 🔴 W3: der Wortlaut war bei der Umstellung
      // gedriftet, dieser Zweig meldete danach `Failed to create case`. Keine
      // Probe hielt ihn, und die Zeile darunter tut es jetzt.
      setError(e.response?.data?.error || 'Failed to create inquiry case')
    } finally {
      setSaving(false)
    }
  }

  // 2026-04-27: handleSendEmail / handleCopyEmail removed alongside the
  // initial-message wizard step. Outbound email now goes through the case
  // detail's AI Composer.

  // ── File upload handlers ────────────────────────────────────────

  const uploadFiles = async (files) => {
    const fileArr = Array.from(files)
    const ts = Date.now()
    const keys = fileArr.map((_, i) => `${ts}_${i}`)
    const controllers = fileArr.map(() => new AbortController())
    setUploadingFiles(prev => [
      ...prev,
      ...fileArr.map((f, i) => ({ key: keys[i], name: f.name, uploading: true, url: null, error: null, controller: controllers[i] })),
    ])
    await Promise.all(fileArr.map(async (file, i) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('order_name', selectedOrder?.name || '')
      fd.append('customer_name', selectedOrder?.customer_name || '')
      fd.append('product_title', selectedLineItem?.title || '')
      try {
        const res = await axios.post(`${API}/api/rma/upload-temp`, fd, { signal: controllers[i].signal })
        setUploadingFiles(prev => prev.map(e =>
          e.key === keys[i] ? { ...e, uploading: false, url: res.data.url, controller: null } : e,
        ))
        setEvidenceUrls(prev => {
          const cleaned = prev.filter(u => u.trim())
          return [...cleaned, res.data.url, '']
        })
      } catch (e) {
        if (axios.isCancel(e) || e.code === 'ERR_CANCELED') {
          setUploadingFiles(prev => prev.filter(x => x.key !== keys[i]))
        } else {
          setUploadingFiles(prev => prev.map(x =>
            x.key === keys[i] ? { ...x, uploading: false, error: 'Upload failed', controller: null } : x,
          ))
        }
      }
    }))
  }

  const handleDragOver  = e => { e.preventDefault(); setDragOver(true) }
  const handleDragLeave = () => setDragOver(false)
  const handleDrop      = e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files) }
  const handleFileSelect = e => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = '' }

  // URL / serial list helpers
  const updateUrl    = (i, v) => setEvidenceUrls(u => u.map((x, idx) => idx === i ? v : x))
  const addUrl       = ()     => setEvidenceUrls(u => [...u, ''])
  const removeUrl    = i      => setEvidenceUrls(u => u.filter((_, idx) => idx !== i))
  const updateSerial = (i, v) => setSerialNumbers(s => s.map((x, idx) => idx === i ? v : x))
  const addSerial    = ()     => setSerialNumbers(s => [...s, ''])
  const removeSerial = i      => setSerialNumbers(s => s.filter((_, idx) => idx !== i))

  // ── Render ────────────────────────────────────────────────────

  const suggested = inboxCtx?.suggested_orders || []
  const ext       = inboxCtx?.extracted || {}

  // Steps JSX — shared between panel mode (no page chrome) and full page mode
  const wizardInner = (
    <>
      <StepIndicator
        activeStep={hasMatchStep ? (matchPhase ? 0 : step + 1) : step}
        isReturn={fallartAusIssueType(issueType) === 'return'}
        isInquiry={isInquiry}
        hasMatchStep={hasMatchStep}
      />

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: SP.card }}>
            {error}
          </Alert>
        )}

        {/* N33: die Belegung ist eine AUSKUNFT mit zwei Wegen, kein Fehlschlag.
            Sie steht UEBER dem Formular, weil die Dringlichkeit aus der
            Reihenfolge kommen soll und nicht aus einem Akzentbalken. */}
        <BelegtePositionPanel
          treffer={belegtVon}
          laeuft={saving}
          onAbbrechen={() => { setBelegtVon(null); setNochmalSenden(null) }}
          onTrotzdem={async () => {
            if (!nochmalSenden) return
            setSaving(true)
            try {
              const angelegt = await nochmalSenden()
              if (!angelegt) return
              setCreatedCase(angelegt)
              // 🔴 W1 des Regel-15-Tors: bis hierher fiel auf diesem Weg der
              // KI-Entwurf aus. Beide regulaeren Zweige feuern nach der Anlage
              // `generate-case-draft` ab; der erzwungene Weg tat es nicht, und
              // ein "trotzdem" angelegter Fall oeffnete ohne vorbereiteten
              // Entwurf. Ausgerechnet der neue Weg war damit der schlechtere.
              //
              // Fire-and-forget wie nebenan: ein Fehlschlag darf die
              // Navigation nicht aufhalten.
              const neueId = angelegt?.id || angelegt?.case?.id
              if (neueId) {
                axios.post(`${API}/api/composer/generate-case-draft`, {
                  case_id: neueId,
                  body_text: issueDescription.trim() || null,
                  inquiry_type: angelegt?.inquiry_type || null,
                  language: angelegt?.customer_language || null,
                }).catch(err => console.warn('[RMA] pre-gen draft failed (non-blocking)', err))
              }
              goToCase(angelegt)
            } catch (e) {
              setError(e.response?.data?.error || 'Failed to create case')
            } finally {
              setSaving(false)
            }
          }}
        />

        {/* ── Match Order pre-step ──────────────────────────── */}
        {matchPhase && matchCtx && (
          <Stack spacing={SP.card}>
            {/* Extracted email info */}
            <Paper variant="outlined" sx={{ p: SP.sm, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" fontWeight={700} display="block" mb={SP.tight} color="text.secondary">
                Extracted from email
              </Typography>
              <Stack spacing={0.5}>
                <SummaryRow label="RT Number" value={matchCtx.extracted?.lsm_rt_number} />
                <SummaryRow label="Reference" value={matchCtx.extracted?.reference} />
                <SummaryRow label="Customer"  value={matchCtx.extracted?.customer_name} />
                <SummaryRow label="Email"     value={matchCtx.extracted?.customer_email} />
                <SummaryRow label="Address"   value={[
                  matchCtx.extracted?.customer_street,
                  [matchCtx.extracted?.customer_zip, matchCtx.extracted?.customer_city].filter(Boolean).join(' '),
                ].filter(Boolean).join(', ') || null} />
                <SummaryRow label="Reason"    value={matchCtx.extracted?.lsm_reason} />
              </Stack>
            </Paper>

            {/* Suggested orders */}
            <Typography variant="subtitle2">Suggested Orders</Typography>
            <Stack spacing={SP.tight}>
              {(matchCtx.suggested_orders || []).map(order => (
                <SuggestedOrderCard
                  key={order.id}
                  order={order}
                  confidence={order.match_confidence}
                  onAccept={o => {
                    setSelectedOrder(o)
                    setOrderFromEmail(true)  // accepted an email-suggested order
                    // Smart case_type from inbox context. Honor all three
                    // suggested types — LSM/return-keyword emails map to
                    // 'return', inquiry intents (status_inquiry, product_advisory,
                    // delivery_status) map to 'inquiry', everything else to
                    // 'other' (service). Previously inquiry was forced into
                    // 'other' — see PR #472 (wizard inquiry tile).
                    if (!issueType && matchCtx?.suggested_case_type) {
                      const sct = matchCtx.suggested_case_type
                      switchIssueType(
                        sct === 'return' ? 'return'
                        : sct === 'inquiry' ? 'inquiry'
                        : 'other'
                      )
                    }
                    setMatchPhase(false)
                    // 2026-07-05 (Samuel feedback): land on step 0 (Case-Type
                    // tiles) instead of jumping straight to step 1 (Product).
                    // The auto-selected type is still highlighted, but staff
                    // sees the picker and can override with a single click —
                    // no more "Back" to find the tiles + lose email context.
                    setStep(0)
                    setError(null)
                  }}
                />
              ))}
            </Stack>

            <Button
              variant="text"
              size="small"
              startIcon={<SearchIcon />}
              onClick={() => setMatchPhase(false)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Search manually instead
            </Button>
          </Stack>
        )}

        {/* ── Step 0: Type picker + Order search OR Inquiry form ─────── */}
        {!matchPhase && step === 0 && (
          <Stack spacing={SP.card}>
            {/* ── Inbox recommendation banner ──────────────────────────────
                Shown when opened from an inbox email with a stored analysis.
                Reads llm_extracted_json.case_type (type signal) — NOT issue_type
                (which is a severity enum). ext.confidence = classification score;
                match_confidence = order-match score (default 0.0). Collapses on
                accept or "Other type".
                bannerActive = banner is visible; gates both this banner and the
                legacy Alert below so only one recommendation surface renders. */}
            {(() => {
              if (!recType || !panelEmail) return null
              const ext = panelEmail.llm_extracted_json || {}
              // Confidence chip: ONLY the classification confidence (ext.confidence).
              // Do NOT fall back to match_confidence — that's the order-match score,
              // defaults 0.0, and rendered a misleading "Confidence 0%" on every email.
              const conf = typeof ext.confidence === 'number' ? ext.confidence : null
              const typeLabel = recType === 'return' ? 'Return' : recType === 'service' ? 'Service' : 'Inquiry'
              const cust = [ext.customer_name, ext.customer_email, ext.customer_country].filter(Boolean).join(' · ')
              return (
                <>
                  <Collapse in={bannerActive} appear timeout={220}>
                    <Box sx={{ ...PANEL_SX, mb: SP.md, borderColor: 'primary.light' }}>
                      <Stack direction="row" alignItems="center" spacing={SP.tight} sx={{ mb: SP.tight }}>
                        <AutoFixHighIcon fontSize="small" color="primary" />
                        <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                          Recommendation from inbox
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={SP.tight} flexWrap="wrap">
                        <Chip label={typeLabel} color="primary" size="small" />
                        {typeof conf === 'number' && conf > 0 && (
                          <Chip label={`Confidence ${Math.round(conf * 100)}%`} size="small" variant="outlined" />
                        )}
                      </Stack>
                      {panelEmail.llm_summary && (
                        <Typography variant="body2" sx={{ mt: SP.tight }}>{panelEmail.llm_summary}</Typography>
                      )}
                      {cust && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{cust}</Typography>
                      )}
                      {/* Product, plus the raw email order ref ONLY when we have no
                          matched Shopify order to show below (avoid duplicate/conflicting order lines). */}
                      {(ext.product_name || (!matchCtx?.suggested_orders?.[0]?.name && ext.order_number)) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {[ext.product_name, (matchCtx?.suggested_orders?.[0]?.name ? null : ext.order_number)].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                      {matchCtx?.suggested_orders?.[0]?.name && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Order {matchCtx.suggested_orders[0].name}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={SP.tight} alignItems="center" sx={{ mt: SP.tight }}>
                        <Button variant="contained" size="small"
                          onClick={() => {
                            switchIssueType(recType === 'service' ? 'other' : recType)  // sets type + arms typeAccepted
                            setRecoDismissed(true)
                            // Gap-based silent order pre-select; else the order section's SuggestedOrderCard confirms.
                            const orders = matchCtx?.suggested_orders || []
                            const sugg = orders[0]
                            const conf0 = sugg?.match_confidence
                            const conf1 = orders[1]?.match_confidence ?? 0
                            const confidentOrder = typeof conf0 === 'number' && conf0 >= 0.88 && (conf0 - conf1) >= 0.15
                            // Advance straight to the filled step with the order pre-selected.
                            // Inquiry has its own selector (pre-fills customer + setStep(1)); the
                            // switchIssueType('inquiry') above cleared selectedOrder + reset to step 0,
                            // so this must run AFTER it (React batches → the later setState wins).
                            if (sugg && confidentOrder) {
                              if (recType === 'inquiry') handleSelectOrderForInquiry(sugg)  // pre-fills customer, advances
                              else handleSelectOrder(sugg, true)                            // email-derived match
                            }
                          }}>
                          Adopt &amp; review
                        </Button>
                        <Button variant="text" size="small" color="inherit"
                          onClick={() => setRecoDismissed(true)}>
                          Adjust
                        </Button>
                      </Stack>
                    </Box>
                  </Collapse>
                  {/* Auto-detection fallback — shown when no banner is active (banner
                      was dismissed or never shown) but a suggested case_type + order
                      are both in play. Suppressed while the banner is visible so only
                      one recommendation surface renders at a time. */}
                  {matchCtx?.suggested_case_type && selectedOrder && !bannerActive && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Suggestion from the inbox: <strong>
                        {matchCtx.suggested_case_type === 'return' ? 'Return'
                          : matchCtx.suggested_case_type === 'inquiry' ? 'Inquiry'
                          : 'Service'}
                      </strong> for order <strong>{selectedOrder.name || '—'}</strong>.
                      Pick a different tile to change, otherwise continue.
                    </Alert>
                  )}
                </>
              )
            })()}
            {/* Type tiles = fallback, hidden while the recommendation banner is the
                primary path (the banner's "Other type" reveals them). */}
            {!bannerActive && (
            <Stack spacing={SP.tight}>
              <Typography variant="subtitle2">Case Type</Typography>
              {/* 🔴 N2b (R6): die fuenf ausgeschriebenen Kachel-Ternaere lesen
                  jetzt `fallartAusIssueType`. Der Leer-Test `issueType &&`
                  MUSS im Service-Zweig stehen bleiben und darf NICHT in
                  `fallartAusIssueType` wandern: dort heisst `''` „Service"
                  (die Frage ist „welche Fallart traegt dieser Wert"), hier
                  heisst `''` „noch nichts gewaehlt". Ohne das Konjunkt waere
                  die Service-Kachel beim Oeffnen des Assistenten markiert.
                  Der Return-Zweig braucht ihn nicht — `fallartAusIssueType('')`
                  ist nicht `'return'`. Bewacht von `anzeige_adoption`. */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.sm}>
                <Box
                  onClick={() => switchIssueType(issueType && fallartAusIssueType(issueType) === 'service' ? '' : 'other')}
                  sx={{
                    flex: 1,
                    p: SP.card,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: issueType && fallartAusIssueType(issueType) === 'service' ? 'info.main' : 'divider',
                    bgcolor: issueType && fallartAusIssueType(issueType) === 'service' ? 'action.selected' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    ...hoverOnly({ borderColor: 'info.main', bgcolor: 'action.hover' }),
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="info.main">Service Request</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Defective, damaged, wrong item, missing parts
                  </Typography>
                </Box>
                <Box
                  onClick={() => switchIssueType('return')}
                  sx={{
                    flex: 1,
                    p: SP.card,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: fallartAusIssueType(issueType) === 'return' ? 'warning.main' : 'divider',
                    bgcolor: fallartAusIssueType(issueType) === 'return' ? 'action.selected' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    ...hoverOnly({ borderColor: 'warning.main', bgcolor: 'action.hover' }),
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="warning.main">Return</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Customer wants to return a product
                  </Typography>
                </Box>
                <Box
                  onClick={() => switchIssueType('inquiry')}
                  sx={{
                    flex: 1,
                    p: SP.card,
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: isInquiry ? 'secondary.main' : 'divider',
                    bgcolor: isInquiry ? 'action.selected' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    ...hoverOnly({ borderColor: 'secondary.main', bgcolor: 'action.hover' }),
                  }}
                >
                  <Stack direction="row" spacing={SP.chip} alignItems="center">
                    <QuestionAnswerOutlinedIcon fontSize="small" color="secondary" />
                    <Typography variant="subtitle2" fontWeight={700} color="secondary.main">
                      Question / Inquiry
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Customer question, no return or service work
                  </Typography>
                </Box>
              </Stack>
            </Stack>
            )}

            {!bannerActive && <Divider />}

            {/* ── Inquiry path: optional order lookup ─────────────────── */}
            {isInquiry && (
              <>
                <Stack spacing={SP.tight}>
                  <Typography variant="subtitle2">Find the customer's order (optional)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pick an order to auto-fill name, email, phone, and address — or skip for a new customer.
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={SP.tight}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search orders by customer name or email…"
                    inputProps={{ 'aria-label': 'Search orders' }}
                    value={searchQ}
                    onChange={e => setSearchQ(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searching ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} color="secondary" />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Stack>

                {searchResults.length > 0 && (
                  <List
                    disablePadding
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    {searchResults.map((order, i) => (
                      <React.Fragment key={order.id}>
                        {i > 0 && <Divider />}
                        <ListItemButton onClick={() => handleSelectOrderForInquiry(order)}>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={SP.tight} alignItems="center">
                                <Typography variant="body2" fontWeight={700}>{order.name}</Typography>
                                {/* A5-4b: `variant="absolute"` (a non-interactive
                                    `<time>`) is required here — the row is a
                                    ListItemButton, and `auto` would nest a
                                    button inside a button. */}
                                <Typography variant="caption" color="text.secondary">
                                  <CaseTime value={order.created_at} variant="absolute" precision="date" />
                                </Typography>
                              </Stack>
                            }
                            secondary={order.customer_name || order.customer_email || 'Guest'}
                          />
                          <ArrowForwardIcon fontSize="small" color="action" />
                        </ListItemButton>
                      </React.Fragment>
                    ))}
                  </List>
                )}

                <Divider>
                  <Typography variant="caption" color="text.disabled">or</Typography>
                </Divider>

                <Stack direction="row" justifyContent="center" pt={SP.tight}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleSkipInquiryOrderLookup}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Skip — new customer / no order
                  </Button>
                </Stack>
              </>
            )}

            {/* ── Service / Return path: suggested-order card (single recommendation surface — B1) ── */}
            {!isInquiry && !bannerActive && !selectedOrder && matchCtx?.suggested_orders?.[0] && !orderSearchOpen && (
              <SuggestedOrderCard
                order={matchCtx.suggested_orders[0]}
                confidence={matchCtx.suggested_orders[0].match_confidence}
                detectedFromEmail
                onAccept={o => handleSelectOrder(o, true)}
                onReject={() => setOrderSearchOpen(true)}
              />
            )}

            {/* ── Service / Return path: Order search ── */}
            {!isInquiry && !bannerActive && !selectedOrder && (orderSearchOpen || !matchCtx?.suggested_orders?.length) && (
              <>
                <Typography variant="subtitle2">Search for the Shopify order</Typography>

                <Stack direction="row" spacing={SP.tight}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search orders by customer name or email…"
                    inputProps={{ 'aria-label': 'Search orders' }}
                    value={searchQ}
                    onChange={e => setSearchQ(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searching ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} color="inherit" />
                        </InputAdornment>
                      ) : null,
                    }}
                    disabled={!issueType}
                  />
                </Stack>

                {searchResults.length > 0 && (
                  <List
                    disablePadding
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    {searchResults.map((order, i) => (
                      <React.Fragment key={order.id}>
                        {i > 0 && <Divider />}
                        <ListItemButton onClick={() => handleSelectOrder(order)}>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={SP.tight} alignItems="center">
                                <Typography variant="body2" fontWeight={700}>{order.name}</Typography>
                                {/* A5-4b: `variant="absolute"` (a non-interactive
                                    `<time>`) is required here — the row is a
                                    ListItemButton, and `auto` would nest a
                                    button inside a button. */}
                                <Typography variant="caption" color="text.secondary">
                                  <CaseTime value={order.created_at} variant="absolute" precision="date" />
                                </Typography>
                              </Stack>
                            }
                            secondary={order.customer_name || order.customer_email || 'Guest'}
                          />
                          <ArrowForwardIcon fontSize="small" color="action" />
                        </ListItemButton>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </>
            )}
          </Stack>
        )}

        {/* ── Step 1 (Inquiry): Question form ────────────────────────── */}
        {!matchPhase && isInquiry && step === 1 && (
          <Stack spacing={SP.md}>

            {/* 1. Original Message — read-only inbox context (panel mode only) */}
            {panelEmail && (
              <InquirySection index={0} icon={<EmailIcon fontSize="small" color="action" />} title="Original Message">
                <Typography variant="body2" fontWeight={600}>
                  {panelEmail.subject || '(no subject)'}
                </Typography>
                {panelEmail.llm_summary && (
                  <Typography variant="caption" color="text.secondary">{panelEmail.llm_summary}</Typography>
                )}
                {(matchCtx?.extracted?.customer_name || matchCtx?.extracted?.customer_email) && (
                  <Typography variant="caption" color="text.secondary">
                    {[matchCtx.extracted.customer_name, matchCtx.extracted.customer_email].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </InquirySection>
            )}

            {/* 2. Request — subject, question summary, AI-Assist accordion */}
            <InquirySection index={1} icon={<QuestionAnswerOutlinedIcon fontSize="small" color="action" />} title="Request">
              <TextField
                fullWidth
                required
                size="small"
                label="Subject / Title"
                placeholder="e.g. Shipping question, product advice, complaint"
                value={inquirySubject}
                onChange={e => setInquirySubject(e.target.value)}
                helperText="Short summary — shown as the case title"
                InputLabelProps={{ shrink: !!inquirySubject || undefined }}
              />

              <TextField
                fullWidth
                required
                multiline
                rows={5}
                size="small"
                label="Question summary"
                value={issueDescription}
                onChange={e => setIssueDescription(e.target.value)}
                placeholder="What did the customer ask? e.g. When will my order arrive?"
                InputLabelProps={{ shrink: true }}
              />

              {/* AI-Assist panel — paste an email (or forward body), let the
                  analyzer populate subject / summary / customer / language /
                  product matches in one click. Optional — leave collapsed to
                  fill in manually. */}
              <Accordion
                expanded={aiExpanded}
                onChange={(_e, ex) => setAiExpanded(ex)}
                sx={{ '&:before': { display: 'none' } }}
                variant="outlined"
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AutoFixHighIcon fontSize="small" color="secondary" />
                    <Typography variant="body2" fontWeight={500}>
                      Analyze from email
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (optional — the LLM fills fields + detects products)
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={SP.tight}>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      size="small"
                      label="Paste the email text here"
                      placeholder="Forwarded customer email, chat message, or original text"
                      value={aiEmailText}
                      onChange={(e) => setAiEmailText(e.target.value)}
                      disabled={aiLoading}
                      helperText={`${aiEmailText.length}/6000 characters`}
                    />
                    {aiError && (
                      <Alert severity="error" onClose={() => setAiError(null)} sx={{ py: 0.5 }}>
                        {aiError}
                      </Alert>
                    )}
                    <Stack direction="row" spacing={SP.tight}>
                      <DisabledReason
                        reason={aiLoading
                          ? null
                          : aiEmailText.trim().length < 20
                            ? 'Paste at least 20 characters of the email first.' : null}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={handleAnalyzeInquiryEmail}
                          disabled={aiLoading || aiEmailText.trim().length < 20}
                          startIcon={
                            aiLoading
                              ? <CircularProgress size={12} color="inherit" />
                              : <AutoFixHighIcon fontSize="small" />
                          }
                        >
                          {aiLoading ? 'Analyzing…' : 'Analyze & fill fields'}
                        </Button>
                      </DisabledReason>
                    </Stack>
                    {productMatches.length > 0 && (
                      <Box sx={{ mt: SP.tight, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Detected products ({productMatches.length}) —
                          deselect what doesn't fit. Selected SKUs are saved as references on the case.
                        </Typography>
                        <Stack>
                          {productMatches.map((m) => (
                            <FormControlLabel
                              key={m.sku || m.raw_hint}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={selectedRefSkus.has(m.sku)}
                                  onChange={() => toggleProductRef(m.sku)}
                                />
                              }
                              label={
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                  <Typography variant="body2">{m.product_title}</Typography>
                                  <Chip label={m.sku} size="small" variant="outlined" sx={{ fontSize: FS.xs, height: 18 }} />
                                  <Chip
                                    label={`${Math.round((m.confidence || 0) * 100)}%`}
                                    size="small"
                                    sx={{ fontSize: FS.xs, height: 18 }}
                                    color={m.confidence >= 0.8 ? 'success' : m.confidence >= 0.5 ? 'warning' : 'default'}
                                  />
                                  {m.source === 'order_link' && (
                                    <Chip label="from order" size="small" color="secondary"
                                      variant="outlined" sx={{ fontSize: FS.xs, height: 18 }} />
                                  )}
                                </Stack>
                              }
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </InquirySection>

            {/* 3. Customer — customer name/email + phone/language */}
            <InquirySection index={2} icon={<PersonIcon fontSize="small" color="action" />} title="Customer">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.tight}>
                <TextField
                  fullWidth
                  size="small"
                  label="Customer name"
                  value={inquiryCustomerName}
                  onChange={e => setInquiryCustomerName(e.target.value)}
                  placeholder="Florian Mustermann"
                  InputLabelProps={{ shrink: !!inquiryCustomerName || undefined }}
                />
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Customer email"
                  type="email"
                  value={inquiryCustomerEmail}
                  onChange={e => setInquiryCustomerEmail(e.target.value)}
                  placeholder="florian@example.de"
                  InputLabelProps={{ shrink: !!inquiryCustomerEmail || undefined }}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.tight}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone (optional)"
                  value={inquiryCustomerPhone}
                  onChange={e => setInquiryCustomerPhone(e.target.value)}
                  placeholder="+49 30 12345678"
                  InputLabelProps={{ shrink: !!inquiryCustomerPhone || undefined }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    label="Language"
                    value={inquiryCustomerLanguage}
                    onChange={e => setInquiryCustomerLanguage(e.target.value)}
                  >
                    <MenuItem value="de">Deutsch</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Typography variant="caption" color="text.secondary">Address</Typography>
              <TextField fullWidth size="small" label="Street + no. (optional)"
                value={inquiryCustomerStreet} onChange={e => setInquiryCustomerStreet(e.target.value)}
                InputLabelProps={{ shrink: !!inquiryCustomerStreet || undefined }} />
              <TextField fullWidth size="small" label="Address line 2 (optional)"
                value={inquiryCustomerLine2} onChange={e => setInquiryCustomerLine2(e.target.value)}
                InputLabelProps={{ shrink: !!inquiryCustomerLine2 || undefined }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={SP.tight}>
                <TextField fullWidth size="small" label="ZIP (optional)"
                  value={inquiryCustomerZip} onChange={e => setInquiryCustomerZip(e.target.value)}
                  InputLabelProps={{ shrink: !!inquiryCustomerZip || undefined }} />
                <TextField fullWidth size="small" label="City (optional)"
                  value={inquiryCustomerCity} onChange={e => setInquiryCustomerCity(e.target.value)}
                  InputLabelProps={{ shrink: !!inquiryCustomerCity || undefined }} />
                <TextField size="small" label="Country" sx={{ minWidth: 100 }}
                  value={inquiryCustomerCountry} onChange={e => setInquiryCustomerCountry(e.target.value)}
                  placeholder="DE" InputLabelProps={{ shrink: !!inquiryCustomerCountry || undefined }}
                  inputProps={{ maxLength: 4 }} />
              </Stack>
            </InquirySection>

            {/* 4. Order — order chip / free-text order ref */}
            <InquirySection index={3} icon={<LinkIcon fontSize="small" color="action" />} title="Order">
              {selectedOrder ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Linked order
                  </Typography>
                  <Chip
                    label={
                      selectedOrder.name +
                      (selectedOrder.line_items?.[0]?.title ? ` — ${selectedOrder.line_items[0].title}` : '')
                    }
                    onDelete={() => setSelectedOrder(null)}
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  label="Related order (optional)"
                  value={inquiryOrderRef}
                  onChange={e => setInquiryOrderRef(e.target.value)}
                  placeholder="SHOP-12345"
                  InputLabelProps={{ shrink: !!inquiryOrderRef || undefined }}
                  helperText="Free text, stored as the case's order reference and not checked against Shopify."
                />
              )}
            </InquirySection>

            <Stack direction="row" spacing={SP.tight} justifyContent="space-between" pt={SP.tight}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => { setStep(0); setError(null) }}
              >
                Back
              </Button>
              <DisabledReason
                reason={saving
                  ? null
                  : !inquiryCustomerEmail.trim() ? 'Enter the customer email first.'
                    : !issueDescription.trim() ? 'Describe the issue first.'
                      : !inquirySubject.trim() ? 'Enter a subject first.' : null}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  endIcon={
                    saving
                      ? <CircularProgress size={16} color="inherit" />
                      : <ArrowForwardIcon />
                  }
                  onClick={handleSubmitInquiry}
                  disabled={
                    saving ||
                    !inquiryCustomerEmail.trim() ||
                    !issueDescription.trim() ||
                    !inquirySubject.trim()
                  }
                >
                  {saving ? 'Creating…' : 'Create inquiry case'}
                </Button>
              </DisabledReason>
            </Stack>
          </Stack>
        )}

        {/* ── Step 1: Product selection (Return / Service only) ─ */}
        {!matchPhase && !isInquiry && step === 1 && selectedOrder && (
          <Stack spacing={SP.md}>
            {/* Order & Product — what order + which line item + how many affected */}
            <InquirySection index={0} icon={<LinkIcon fontSize="small" color="action" />} title="Order & Product">
              <Stack direction="row" spacing={SP.tight} alignItems="center" flexWrap="wrap">
                <Chip label={selectedOrder.name} size="small" />
                {orderFromEmail && (
                  <Chip
                    icon={<AutoFixHighIcon />}
                    label="detected from email"
                    size="small"
                    variant="outlined"
                    color="primary"
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {selectedOrder.customer_name}
                </Typography>
              </Stack>

              <Typography variant="subtitle2">Select the affected product</Typography>

              <List
                disablePadding
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                {selectedOrder.line_items.map((li, i) => (
                  <React.Fragment key={li.id}>
                    {i > 0 && <Divider />}
                    <ListItemButton
                      selected={selectedLineItem?.id === li.id}
                      onClick={() => {
                        setSelectedLineItem(li)
                        setQuantityAffected(mengenVorbelegung(li))
                        setQuantityTouched(false)
                      }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <span>{li.title}</span>
                            {li.fulfillment_status === 'fulfilled' && (
                              <Chip label="Fulfilled" size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                            )}
                            {li.fulfillment_status === 'partial' && (
                              // K-3: umrandet = Schrift `palette.warning.main` auf
                              // Papier (3,113:1 hell); der v2-Ton traegt in beiden Modi.
                              <Chip label="Partial" size="small" variant="outlined" sx={(t) => ({ color: v2Tone(t, 'warn').main, borderColor: v2Tone(t, 'warn').main, height: 16, fontSize: 10 })} />
                            )}
                            {(li.fulfillment_status === 'unfulfilled' || !li.fulfillment_status) && (
                              <Chip label="Not fulfilled" size="small" color="default" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                            )}
                            {li.fulfillment_status === 'restocked' && (
                              <Chip label="Restocked / removed" size="small" color="error" variant="outlined" sx={{ height: 16, fontSize: 10 }} />
                            )}
                          </Stack>
                        }
                        secondary={[li.variant_title, li.sku, li.vendor].filter(Boolean).join(' · ')}
                      />
                      <Stack alignItems="flex-end" sx={{ ml: 1, flexShrink: 0 }}>
                        {li.unit_price > 0 && (
                          <Typography variant="body2" fontWeight={600}>{formatMoney(li.unit_price)}</Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">×{li.quantity}</Typography>
                      </Stack>
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>

              {selectedLineItem && (
                // W2 (Regel-16-Gate): `flexWrap` wie die fuenf Geschwister-Stacks
                // derselben Datei — der neue war der einzige ohne. Die feste
                // Breite ist weg (`pattern-library.md:544`: keine festen
                // Eingabebreiten); auf Schmalgeraeten nimmt das Feld die ganze
                // Zeile, ab `sm` bleibt es kompakt.
                <Stack direction="row" spacing={SP.card} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                  <TextField
                    // W4: die Beschriftung nennt jetzt die Sache, nicht die
                    // Systemgroesse. Gefragt ist, wie viele Stueck ZURUECKKAMEN
                    // — so sagt es auch `menge.js` („how many units came back").
                    label="Units returned"
                    type="number"
                    size="small"
                    // W5: die Wache gilt fuer JEDE gewaehlte Position, nicht nur
                    // bei bestellt > 1 — leert man das vorbelegte Feld bei
                    // bestellt = 1, sperrt „Next" ebenfalls. `required` sagte
                    // dort „optional" und liess einen gesperrten Knopf
                    // unerklaerbar zurueck.
                    required={!!selectedLineItem}
                    error={mengeFehler}
                    value={quantityAffected}
                    inputRef={mengeRef}
                    onChange={e => {
                      setQuantityTouched(true)
                      // 🔴 Nach UNTEN wird nicht mehr geklemmt. Die alte Fassung
                      // war `Math.max(1, Math.min(parseInt(e.target.value) || 1, …))`
                      // — und `parseInt('') || 1` ist **1**, der Wert also nie
                      // leer. Ein Pflichtfeld, das nicht leer sein kann, ist
                      // keines; der Plan hat genau diesen Irrtum am 2026-09-12
                      // an sich selbst korrigiert. Nach oben bleibt die Grenze:
                      // mehr als bestellt kann nicht stimmen, und der Server
                      // antwortet dort 422 (`quantity_above_ordered`).
                      const roh = e.target.value
                      if (roh === '') { setQuantityAffected(''); return }
                      const zahl = parseInt(roh, 10)
                      if (Number.isNaN(zahl)) { setQuantityAffected(''); return }
                      setQuantityAffected(Math.min(Math.max(zahl, 1), selectedLineItem.quantity))
                    }}
                    inputProps={{ min: 1, max: selectedLineItem.quantity }}
                    // 🔴 B1/B3/W4 des Regel-16-Gates, in einem Zug. Vorher stand
                    // hier `aria-describedby="n24-menge-hinweis"` neben einem
                    // roten `Typography`. Beides war falsch, und zwar gemessen:
                    //
                    // MUI streut unbekannte Props ueber `...other` in den
                    // ROOT-Slot (`FormControl`) und setzt am `input` selbst hart
                    // `aria-describedby={helperTextId}`. Der Verweis hing damit
                    // am Wrapper-`div`, und die TATSAECHLICHE Beschreibung des
                    // Felds war `' '` — ein Leerzeichen. Am Vorlesegeraet erfuhr
                    // niemand, dass vier Stueck bestellt sind; lesbar wurde es
                    // erst im FEHLERzustand, also zu spaet.
                    //
                    // Dazu verbietet `pattern-library.md:545` rotes `Typography`
                    // neben dem Feld woertlich: „Use MUI's built-in error
                    // styling". Im Fehlerfall sagten zwei Kanaele dasselbe,
                    // waehrend der Kanal, der VORHER traegt, stumm war.
                    //
                    // Jetzt: ein Text, immer da, von MUI selbst mit dem
                    // Eingabefeld verknuepft — und er stellt die FRAGE, statt
                    // eine Systemgroesse zu nennen.
                    helperText={
                      mengeFehler
                        ? `Required — how many of ${selectedLineItem.quantity} came back?`
                        : mengePflicht
                          ? `How many of ${selectedLineItem.quantity} came back?`
                          : `of ${selectedLineItem.quantity} ordered`
                    }
                    sx={{ width: { xs: '100%', sm: 220 } }}
                  />
                </Stack>
              )}
            </InquirySection>

            {/* Case Details — type badge (read-only; locked at Step 0) + issue type */}
            <InquirySection index={1} icon={<QuestionAnswerOutlinedIcon fontSize="small" color="action" />} title="Case Details">
              <Stack direction="row" spacing={SP.tight}>
                {/* N2b (A7): Text und Farbe kamen aus zwei parallelen
                    Dreiweg-Ternaeren — die vierte Darstellungsregel. Sie sind
                    jetzt EINE Ableitung plus eine Karte. Wertgleich auch fuer
                    `issueType === ''`: `fallartAusIssueType('')` ist `'service'`,
                    genau der fruehere Else-Zweig. */}
                <Chip
                  size="small"
                  label={WIZARD_FALLART_CHIP[fallartAusIssueType(issueType)].label}
                  color={WIZARD_FALLART_CHIP[fallartAusIssueType(issueType)].color}
                  variant="outlined"
                />
              </Stack>

              {/* Issue Type only applies to SERVICE cases — a return has no sub-type
                  (the single "Return" option was pure noise). issueType is already
                  'return' from the tile, so returns skip this entirely. Services keep
                  it (prefilled to 'Other', staff refines).

                  🔴 D12 (2026-09-09): `required` an der FormControl traegt
                  `aria-required`. Bis dahin war das Sternchen im sichtbaren
                  Etikett der EINZIGE Kanal fuer „Pflicht" — ein Screenreader
                  las „Issue Type Stern" (WCAG 3.3.2). Das Feld ist seit D12 das
                  unumgehbare Tor der Posteingangsbahn: ein unbekannter
                  Modellwert wird nicht mehr vorbelegt, also MUSS hier gewaehlt
                  werden. */}
              {selectedLineItem && fallartAusIssueType(issueType) !== 'return' && (
                <FormControl fullWidth size="small" required>
                  <InputLabel>Issue Type *</InputLabel>
                  <Select
                    value={issueType}
                    label="Issue Type *"
                    onChange={e => setIssueType(e.target.value)}
                  >
                    {ISSUE_TYPE_OPTIONS.filter(o => o.group === 'Service Case').map(o => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </InquirySection>

            <Stack direction="row" spacing={SP.tight} justifyContent="space-between" pt={SP.tight}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => { setStep(0); setSelectedOrder(null); setSearchResults([]) }}
              >
                Back
              </Button>
              <DisabledReason
                reason={!selectedLineItem ? 'Select a line item first.'
                  : !issueType ? 'Choose an issue type first.'
                    : !mengeGueltig ? 'Enter a valid quantity.' : null}
              >
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleProceedToStep3}
                  disabled={!selectedLineItem || !issueType || !mengeGueltig}
                >
                  Next
                </Button>
              </DisabledReason>
            </Stack>
          </Stack>
        )}

        {/* ── Step 2: Issue details (Return / Service only) ─── */}
        {!matchPhase && !isInquiry && step === 2 && (
          <Stack spacing={SP.md}>
            <Stack direction="row" spacing={SP.tight} flexWrap="wrap">
              <Chip label={selectedOrder.name} size="small" />
              <Chip label={selectedLineItem.title} size="small" variant="outlined" />
            </Stack>

            {/* Issue Details — description + evidence attachments/links + serial numbers */}
            <InquirySection index={0} icon={<QuestionAnswerOutlinedIcon fontSize="small" color="action" />} title="Issue Details">
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Describe the issue"
              value={issueDescription}
              onChange={e => setIssueDescription(e.target.value)}
              placeholder={aiDescLoading ? 'Generating draft from email…' : 'What exactly is wrong? When did it occur? Any error messages?'}
              InputLabelProps={{ shrink: true }}
              helperText={
                aiDescLoading
                  ? 'Drafting from email…'
                  : aiDescSource === 'llm'
                    ? 'Pre-filled from email by AI — edit as needed'
                    : aiDescSource === 'template'
                      ? 'Pre-filled from email metadata — edit as needed'
                      : ''
              }
            />

            {/* Attachments proposed from the inbound email — pre-checked */}
            {proposedAttachments.length > 0 && (
              <Box
                sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 2,
                  bgcolor: 'action.hover', p: SP.sm,
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" mb={SP.chip}>
                  Attachments from email — uncheck to skip
                </Typography>
                <Stack spacing={SP.chip}>
                  {proposedAttachments.map(att => {
                    const checked = attachmentIds === null || attachmentIds.has(att.id)
                    return (
                      <Stack key={att.id} direction="row" alignItems="center" spacing={SP.chip}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggleAttachment(att.id)}
                          sx={{ p: 0.25 }}
                        />
                        <Typography
                          component="a"
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {att.name}
                        </Typography>
                        {att.size != null && (
                          <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                            {formatBytes(att.size)}
                          </Typography>
                        )}
                      </Stack>
                    )
                  })}
                </Stack>
              </Box>
            )}

            {/* Drag & drop upload */}
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={SP.chip}>
                Evidence — photos, videos, PDFs
              </Typography>

              {/* Drop zone */}
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: dragOver ? 'action.hover' : 'transparent',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  mb: SP.tight,
                  userSelect: 'none',
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {isMobile ? 'Tap to upload photos, videos or PDFs' : 'Drag & drop files here, or click to select'}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Images · Videos · PDFs
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  multiple
                  accept="image/*,video/*,.pdf"
                  onChange={handleFileSelect}
                />
              </Box>

              {/* Upload progress + result chips */}
              {uploadingFiles.length > 0 && (
                <Stack spacing={SP.chip} mb={SP.tight}>
                  {uploadingFiles.map(f => (
                    <Stack key={f.key} direction="row" spacing={SP.chip} alignItems="center">
                      {f.uploading ? (
                        <>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220, flex: 1 }}>
                            {f.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => f.controller?.abort()}
                            sx={{ p: 0.25 }}
                            title="Cancel upload"
                          >
                            <CloseIcon sx={{ fontSize: FS.md }} />
                          </IconButton>
                        </>
                      ) : f.error ? (
                        <Typography variant="caption" color="error">
                          {f.name}: {f.error}
                        </Typography>
                      ) : (
                        <Chip
                          size="small"
                          label={f.name}
                          component="a"
                          href={f.url}
                          target="_blank"
                          clickable
                          color="success"
                          variant="outlined"
                          sx={{ maxWidth: 320 }}
                        />
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}

              {/* Manual link fields — secondary */}
              <Typography variant="caption" color="text.disabled" display="block" mb={SP.chip}>
                Or paste a link (Google Drive, Dropbox…)
              </Typography>
              <Stack spacing={SP.tight}>
                {evidenceUrls.map((url, i) => (
                  <Stack key={i} direction="row" spacing={SP.chip}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="https://drive.google.com/…"
                      value={url}
                      onChange={e => updateUrl(i, e.target.value)}
                    />
                    {evidenceUrls.length > 1 && (
                      <IconButton size="small" onClick={() => removeUrl(i)}
                        aria-label={`Remove evidence link ${i + 1}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addUrl}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add link
                </Button>
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={SP.chip}>
                Serial numbers (optional)
              </Typography>
              <Stack spacing={SP.tight}>
                {serialNumbers.map((sn, i) => (
                  <Stack key={i} direction="row" spacing={SP.chip}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="SN123456"
                      value={sn}
                      onChange={e => updateSerial(i, e.target.value)}
                    />
                    {serialNumbers.length > 1 && (
                      <IconButton size="small" onClick={() => removeSerial(i)}
                        aria-label={`Remove serial number ${i + 1}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addSerial}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add serial
                </Button>
              </Stack>
            </Box>
            </InquirySection>

            <Stack direction="row" spacing={SP.tight} justifyContent="space-between" pt={SP.tight}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => setStep(1)}>Back</Button>
              <Button
                variant="contained"
                endIcon={
                  saving
                    ? <CircularProgress size={16} color="inherit" />
                    : <ArrowForwardIcon />
                }
                onClick={handleProceedToStep4}
                disabled={saving}
              >
                {saving ? 'Creating…' : 'Create case'}
              </Button>
            </Stack>
          </Stack>
        )}

        {/* Step 3 (initial-message preview & send) removed 2026-04-27.
            Outbound email moved to the case detail's AI Composer. */}
    </>
  )

  // ── Panel mode: lean render — no page chrome, inbox stays visible ─────────
  if (isPanelMode) {
    const ex  = panelEmail?.llm_extracted_json || {}
    const ctx = panelCtx?.extracted || {}
    const addressLine = [
      ctx.customer_street,
      [ctx.customer_zip, ctx.customer_city].filter(Boolean).join(' '),
    ].filter(Boolean).join(', ')
    const summaryRows = [
      { label: 'From',       value: panelEmail?.from_name ? `${panelEmail.from_name} <${panelEmail.from_email}>` : panelEmail?.from_email },
      { label: 'Subject',    value: panelEmail?.subject },
      { label: 'RT Number',  value: ctx.lsm_rt_number },
      { label: 'Reference',  value: ctx.reference || ex.order_number },
      { label: 'Customer',   value: ctx.customer_name || ex.customer_name },
      { label: 'Email',      value: ctx.customer_email || ex.customer_email },
      { label: 'Address',    value: addressLine || null },
      { label: 'Reason',     value: ctx.lsm_reason },
      { label: 'Product',    value: ex.product_name },
      // 🔴 N2b: der ZWILLING von `RMAInbox.jsx` — dieselbe Zusammenfassung
      // desselben LLM-Werts, zweimal geschrieben. Die Inventur kannte nur
      // einen der beiden (A6). Jetzt lesen beide dieselbe Regel.
      { label: 'Issue',      value: issueTypeAnzeige(ex.issue_type, { fallback: null }) },
    ].filter(r => r.value)

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>

        {/* ── Email summary card ─────────────────────────────────── */}
        {panelEmail && (
          <Box sx={{
            px: SP.card, pt: SP.card, pb: SP.sm,
            bgcolor: 'background.default',
            borderBottom: '1px solid', borderColor: 'divider',
          }}>
            {/* LLM one-liner */}
            {panelEmail.llm_summary && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic" mb={1}>
                {panelEmail.llm_summary}
              </Typography>
            )}

            {/* Extracted fields */}
            {summaryRows.length > 0 && (
              <Stack spacing={0.4}>
                {summaryRows.map(({ label, value }) => (
                  <Stack key={label} direction="row" spacing={1}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, flexShrink: 0 }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                      {value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {/* Issue description */}
            {ex.issue_description && (
              <Box sx={{
                mt: 1, p: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid', borderColor: 'divider',
              }}>
                <Typography variant="caption" color="text.secondary" fontStyle="italic">
                  "{ex.issue_description}"
                </Typography>
              </Box>
            )}

            {/* Body snippet */}
            {!panelEmail.llm_summary && !summaryRows.length && panelEmail.body_text && (
              <Typography variant="caption" color="text.secondary" sx={{
                display: 'block', whiteSpace: 'pre-wrap', maxHeight: 80, overflow: 'hidden',
                fontFamily: 'monospace', fontSize: '10px',
              }}>
                {panelEmail.body_text.substring(0, 400)}
              </Typography>
            )}
          </Box>
        )}

        {/* ── Wizard steps ───────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: SP.card, p: SP.card }}>
          {wizardInner}
        </Box>
      </Box>
    )
  }

  // ── Modal mode: lean render — Dialog/Drawer provides chrome ──────────────
  // No PageContainer, no ViewHeader, no Cancel action (modal X handles it).
  // Page-mode title shows as a plain heading at the top so the user has a
  // clear "you are creating a case" anchor inside the modal.
  if (modalMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: SP.card }}>
        <Typography variant="h6" fontWeight={700} sx={{ pr: 5 /* clear close-X */ }}>
          New After-Sales Case
        </Typography>
        {wizardInner}
      </Box>
    )
  }

  // ── Page mode: full layout with optional inbox reference panel ────────────
  return (
    <PageContainer boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <ViewHeader
        title="New After-Sales Case"
        breadcrumbs={[{ label: 'After-Sales', href: '/rma' }, { label: 'New Case' }]}
        actions={[{
          key: 'cancel', label: 'Cancel', variant: 'outlined',
          onClick: () => navigate(inboxId ? '/rma/inbox' : '/rma'),
        }]}
      />

      <Stack
        direction={{ xs: 'column', md: inboxId ? 'row' : 'column' }}
        spacing={SP.section}
        alignItems="flex-start"
      >
        {/* ── Reference panel — only when opened from inbox (page mode) ── */}
        {inboxId && (
          <Panel sx={{
            width: { xs: '100%', md: 300 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: 16 },
          }}>
            <Stack spacing={SP.sm}>
              <Typography variant="subtitle2" fontWeight={700}>Email Reference</Typography>
              {inboxCtxLoading ? (
                <Stack alignItems="center" py={2}><CircularProgress size={20} /></Stack>
              ) : (
                <>
                  <Stack spacing={0.5}>
                    {ext.customer_name  && <Typography variant="caption"><strong>Name:</strong> {ext.customer_name}</Typography>}
                    {ext.customer_email && <Typography variant="caption"><strong>Email:</strong> {ext.customer_email}</Typography>}
                    {ext.customer_zip   && <Typography variant="caption"><strong>ZIP:</strong> {ext.customer_zip}</Typography>}
                    {ext.reference      && <Typography variant="caption"><strong>Reference:</strong> {ext.reference}</Typography>}
                  </Stack>
                  {(ext.customer_name || ext.customer_email || ext.customer_zip || ext.reference) && <Divider />}
                  {suggested.length > 0 ? (
                    <>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Matching orders</Typography>
                      <Stack spacing={SP.chip}>
                        {suggested.map(order => (
                          <Box key={order.id} onClick={() => handleSelectOrder(order)} sx={{
                            p: SP.chip, borderRadius: 1, border: '1px solid', cursor: 'pointer',
                            borderColor: selectedOrder?.id === order.id ? 'primary.main' : 'divider',
                            bgcolor: selectedOrder?.id === order.id ? 'primary.50' : 'background.paper',
                            ...hoverOnly({ borderColor: 'primary.main', bgcolor: 'action.hover' }),
                          }}>
                            <Typography variant="body2" fontWeight={700}>{order.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{order.customer_name || order.customer_email}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">No matching orders found.</Typography>
                  )}
                  {inboxCtx?.email?.body_preview && (
                    <>
                      <Divider />
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{inboxCtx.email.subject || '(no subject)'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto', fontFamily: 'monospace', fontSize: '10px' }}>
                        {inboxCtx.email.body_preview}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Stack>
          </Panel>
        )}

        {/* ── Wizard ── */}
        <Panel
          sx={{ ...(inboxId ? { flex: 1, minWidth: 0 } : { maxWidth: 680, mx: 'auto', width: '100%' }) }}
        >
          {wizardInner}
        </Panel>
      </Stack>
    </PageContainer>
  )
}
